"use client";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { IconMinus, IconPlus } from "@tabler/icons-react";
import {
  ChangeEvent,
  KeyboardEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

interface NumberStepperProps {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  decreaseLabel: string;
  increaseLabel: string;
  className?: string;
}

const clamp = (v: number, min: number, max: number) =>
  Math.min(Math.max(v, min), max);

const inputWidthFor = (max: number) => {
  const digits = String(Math.max(Math.abs(max), 1)).length;
  if (digits <= 2) return "w-12";
  if (digits === 3) return "w-14";
  return "w-16";
};

const NumberStepper = ({
  label,
  value,
  min,
  max,
  onChange,
  onBlur,
  onFocus,
  decreaseLabel,
  increaseLabel,
  className,
}: NumberStepperProps) => {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState(String(value));

  // Re-sync the draft whenever the canonical value changes externally
  // (parent reset, +/- buttons, building-type swap). Skip while the user
  // is actively typing to avoid clobbering their in-progress entry.
  useEffect(() => {
    if (document.activeElement !== inputRef.current) {
      setDraft(String(value));
    }
  }, [value]);

  const commit = () => {
    onBlur?.();
    if (draft === "") {
      setDraft(String(value));
      return;
    }
    const parsed = parseInt(draft, 10);
    if (Number.isNaN(parsed)) {
      setDraft(String(value));
      return;
    }
    const next = clamp(parsed, min, max);
    if (next !== value) onChange(next);
    setDraft(String(next));
  };

  const handleDecrement = () => {
    if (value > min) onChange(value - 1);
  };

  const handleIncrement = () => {
    if (value < max) onChange(value + 1);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    if (next === "" || /^\d+$/.test(next)) {
      setDraft(next);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.currentTarget.blur();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      handleIncrement();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      handleDecrement();
    }
  };

  return (
    <div
      className={cn(
        "flex items-center justify-between rounded-lg border px-4 py-3",
        className,
      )}
    >
      <label
        htmlFor={inputId}
        className="cursor-pointer text-sm font-medium select-none"
      >
        {label}
      </label>
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="size-9 rounded-full border"
          disabled={value <= min}
          onClick={handleDecrement}
          aria-label={decreaseLabel}
        >
          <IconMinus className="size-4" />
        </Button>
        <input
          id={inputId}
          ref={inputRef}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="off"
          value={draft}
          onChange={handleChange}
          onBlur={commit}
          onFocus={(e) => {
            onFocus?.();
            e.currentTarget.select();
          }}
          onKeyDown={handleKeyDown}
          role="spinbutton"
          aria-label={label}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={value}
          className={cn(
            "selection:bg-primary selection:text-primary-foreground border-input h-9 cursor-text rounded-md border bg-transparent text-center text-sm font-semibold tabular-nums shadow-xs transition-[color,box-shadow,background-color] outline-none",
            "hover:bg-accent",
            "focus-visible:border-primary focus-visible:ring-primary/50 focus-visible:bg-background focus-visible:ring-[3px]",
            inputWidthFor(max),
          )}
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="size-9 rounded-full border"
          disabled={value >= max}
          onClick={handleIncrement}
          aria-label={increaseLabel}
        >
          <IconPlus className="size-4" />
        </Button>
      </div>
    </div>
  );
};

export default NumberStepper;
